// src/services/impl/UserServiceImpl.ts

import { notificationEndpoints, userEndpoints } from "@/api/endpoints";
import type { FetcherService } from "@/services/abstract/FetcherService";
import { UserService } from "@/services/abstract/UserService";
import type { Notification, User } from "@/types/user/User";

/**
 * Implementation of user service for managing current user profile
 * and notifications.
 *
 * Provides functionality for:
 * - Retrieving current user information
 * - Updating user profile details
 * - Fetching user notifications
 *
 * @see UserService
 */
export class UserServiceImpl extends UserService {
  /**
   * Creates an instance of UserServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  /**
   * Retrieves the current authenticated user's profile information
   * @endpoint GET /user/me
   * @returns User object containing current user's profile data
   * @throws UnauthorizedError if user is not authenticated
   * @tags user, profile
   */
  async getMe(id:string): Promise<User> {
    return await this.fetcher.get<User>(userEndpoints.getUser(id));
  }

  /**
   * Updates the current authenticated user's profile information
   *
   * Allows partial updates - only provided fields will be modified.
   * Omitted fields will remain unchanged.
   *
   * @endpoint PATCH /user/me
   * @param payload - User profile fields to update
   * @param payload.name - Optional new display name
   * @param payload.phone - Optional new phone number
   * @param payload.department - Optional new department
   * @request { name?: string; phone?: string; department?: string }
   * @returns void
   * @throws UnauthorizedError if user is not authenticated
   * @throws ValidationError if provided data is invalid
   * @tags user, profile, update
   */
  async updateMe(id: string, name?: string, phone?: string, department?: string): Promise<void> {
    await this.fetcher.patch(userEndpoints.updateUser(id), { name, phone, department });
  }

  /**
   * Retrieves the current user's notifications
   * @endpoint GET /user/notifications
   * @returns Notifications object containing user's notification data
   * @throws UnauthorizedError if user is not authenticated
   * @tags user, notifications
   */
  async getNotifications(): Promise<Notification> {
    return await this.fetcher.get<Notification>(notificationEndpoints.getNotifications);
  }

  async getEncapsKey(id: string): Promise<{ encapsKey: string }> {
    return await this.fetcher.get<{ encapsKey: string }>(userEndpoints.getEncapsKey(id));
  }

  async getDecapsKey(id: string): Promise<{ decapsKey: string }> {
    return await this.fetcher.get<{ decapsKey: string }>(userEndpoints.getDecapsKey(id));
  }
}
